// The Parts page's reads and its flag / photo actions: ONE copy, used by the desktop Parts page
// (pages/PartEntryPage.jsx) and the phone (mobile/screens/PartsScreen.jsx).
// docs/mobile-pwa-plan.md "One copy of every action". Moved from the page;
// pages/PartEntryPage.flagPhoto.actions.test.jsx pins the actions. Create / edit, revisions and
// approvals, PPAP, costs, DXF and bulk entry stay on the desktop.
import config from '../../config.js';
import { apiErrorMessage, parseApiError } from '../../utils/apiError';

const API_BASE = config.API_BASE_URL;

/** GET /parts/{id}: the part (image_path, flag fields, stored costs + rollup, revision, assemblies). Throws. */
export async function loadPart(partId) {
  const res = await fetch(`${API_BASE}/parts/${partId}`);
  if (!res.ok) throw new Error(await apiErrorMessage(res));
  return res.json();
}

/** GET /parts/{id}/documents (drawings and files; file_url opens them); null when unreadable. */
export async function loadPartDocuments(partId) {
  try {
    const res = await fetch(`${API_BASE}/parts/${partId}/documents`);
    return res.ok ? await res.json() : null;
  } catch (err) {
    console.error('Failed to load documents', err);
    return null;
  }
}

/** GET /parts/{product_id}/revisions: every revision ({part_id, revision_code, is_current_revision,
 *  created_at, ppap_status}); null when unreadable. */
export async function loadPartRevisions(productId) {
  try {
    const res = await fetch(`${API_BASE}/parts/${encodeURIComponent(productId)}/revisions`);
    return res.ok ? await res.json() : null;
  } catch (_) {
    return null;
  }
}

/** The process steps as listed on the part, without Fabrication (the page's routing list). */
export const partProcessSteps = (part) => String(part?.process_steps || '').split(',').map((s) => s.trim()).filter((s) => s && s !== 'Fabrication');

/** A part that is not the current revision is read only (no flag, no photo). */
export const isArchivedRevision = (part) => part?.is_current_revision === false;

/** Flag a part for review (reason, then the flagger's name) or clear it (after asking). The name is
 *  REQUIRED: jobq-api refuses a flag without one ("flagged_by_name is required"), so the question starts
 *  with the admin session's name and a blank answer stops (until 2026-10-14 the page called it optional
 *  and the API refused the save). Resolves {done, payload?} (payload = the fields saved, to merge). */
export async function setPartFlag({ partId, on, readOnly = false, ui, actor = null }) {
  if (!partId) {
    await ui.alert('Save or select a part first before flagging.');
    return { done: false };
  }
  if (readOnly) {
    await ui.alert('Cannot change flags on archived versions.');
    return { done: false };
  }
  let payload;
  if (on) {
    const reason = await ui.prompt('Enter flag reason:');
    if (!reason) return { done: false };
    const userName = String((await ui.prompt('Enter your name:', actor || '')) || '').trim();
    if (!userName) {
      await ui.alert('A name is needed to flag a part.');
      return { done: false };
    }
    payload = { flagged: true, flag_reason: reason, flagged_by_name: userName, flagged_at: new Date().toISOString() };
  } else {
    if (!(await ui.confirm('Clear the flag and reset reason?'))) return { done: false };
    payload = { flagged: false, flag_reason: null, flagged_by_name: null, flagged_at: null };
  }
  try {
    const res = await fetch(`${API_BASE}/parts/${partId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await apiErrorMessage(res));
    await ui.alert(`Part ${on ? 'flagged' : 'unflagged'}.`);
    return { done: true, payload };
  } catch (err) {
    await ui.alert(parseApiError(err.message, 'Error updating flag'));
    return { done: false };
  }
}

/** The 3000 × 3000 limit on a part image; resolves null when it fits, else the sentence to say. */
export function partImageProblem(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img.width > 3000 || img.height > 3000 ? 'Image is larger than 3000x3000. Please resize or pick a smaller screenshot.' : null);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve('Failed to load image. Please select a valid image file.');
    };
    img.src = objectUrl;
  });
}

/** A phone photo's file name: the API stores a part image under its file name, and every iPhone shot is
 *  "image.jpg", so each one would replace the last at the same address. */
export const partPhotoName = (partId, now = Date.now()) => `part-${partId}-${now}.jpg`;

/** Upload a part's image (POST /parts/{id}/upload-image, multipart `file`); the API saves it on the part at
 *  once. "Image uploaded!" through ui.alert; a refusal comes back as `error` (the desktop's banner).
 *  `readOnly` (an archived revision: the API itself doesn't refuse one) stops it. Resolves
 *  {done, imageUrl?, error?}. */
export async function uploadPartImage({ partId, file, ui, readOnly = false }) {
  if (!partId) { await ui.alert('Cannot upload image until part is saved/created.'); return { done: false }; }
  if (readOnly) { await ui.alert('Cannot change the image on archived versions.'); return { done: false }; }
  if (!file) { await ui.alert('No file selected.'); return { done: false }; }
  try {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/parts/${partId}/upload-image`, { method: 'POST', body: formData });
    if (!res.ok) throw new Error(await apiErrorMessage(res));
    const data = await res.json();
    await ui.alert('Image uploaded!');
    return { done: true, imageUrl: data.image_url };
  } catch (err) {
    return { done: false, error: err.message };
  }
}
